/**
 * The one place the tasks phase names its server entities and reads a parent off a surface's params.
 *
 * The two entity constants are aliases for the enum members rather than direct uses, because that is
 * what let the whole web side be built against the contract before the enum carried them: every
 * consumer imports these, so adopting the real members was a one-line change here.
 */
import { ActivityEntityName, Entity } from '@jms/model';
import { PlannedTaskParent, PlannedTaskParentType, PlannedTaskScope } from '@jms/model/entities/planned.work/planned.task/planned.task';

/** One record for a live task, a template task or a Visit exception. */
export const PLANNED_TASK_ENTITY = ActivityEntityName.PlannedTask;

/** The read projection the outline tree queries, for a Service Plan, a Template or a Visit parent. */
export const PLANNED_TASK_OUTLINE_ENTITY = ActivityEntityName.PlannedTaskOutline;

/** Every parent a task may hang off, and whether that parent is a definition rather than a contract. */
export const PLANNED_TASK_TEMPLATE_PARENTS: readonly PlannedTaskParentType[] = [ActivityEntityName.ServiceLineTemplate, ActivityEntityName.ServiceLineTaskTemplate];

/**
 * A template parent carries a cadence but no calendar: `startsOn`, `lastCompleted` and
 * `scheduleExceptions` all name dates a definition reused by a contract starting in any year cannot
 * anchor, and the server rejects them outright.
 */
export function isPlannedTaskTemplateParent(parent: PlannedTaskParent | null | undefined): boolean {

  return !!parent && PLANNED_TASK_TEMPLATE_PARENTS.includes(parent.type);
}

/** True when the parent is another task rather than a line — the same two-level rule the outline draws. */
export function isPlannedTaskSubtaskParent(parent: PlannedTaskParent | null | undefined): boolean {

  return parent?.type === ActivityEntityName.ServiceLineTask || parent?.type === ActivityEntityName.ServiceLineTaskTemplate;
}

/**
 * What a Form or Detail surface is mounted against.
 *
 * The parent pointer replaces the `serviceLine` / `templateLine` / `visitId` params the two old task
 * entities each read differently: one entity, one param shape, whatever owns the task.
 */
export interface PlannedTaskSurfaceParams {
  /** The owner the task is written against. A Visit-scoped row still points at its standing parent. */
  parent?: PlannedTaskParent;
  /** Present only on a Visit mount: the write becomes an exception scoped to this attendance. */
  visitId?: string;
  /** The line the schedule editor resolves its calendar from. Absent on a template or Visit mount. */
  serviceLine?: Entity;
  readonly?: boolean;
  onSaved?: () => Promise<void>;
}

/** The scope a mount writes at: an exception for one attendance, else the standing plan record. */
export function plannedTaskScopeFor(visitId: string | undefined): PlannedTaskScope {

  return visitId ? PlannedTaskScope.Visit : PlannedTaskScope.Standing;
}
